import { createSelector } from '@reduxjs/toolkit';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import { OneTimeCyberAgentScanStatusData, UnauthOneTimeCyberAgentScanStatusData } from 'mcsclient/models';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';

import { IFamilyMember, MonitoringDataType, MonitoringDataTypeMetadata, MonitoringDataTypeNames } from './identityMonitoring.types';
import { getMonitoringDataCount } from './identityMonitoringHelpers';
import { IIdentityMonitoringState } from './identityMonitoringState';

export interface IMonitoringDataTypeGroup {
  monitoredTypeNames: MonitoringDataType[];
  addableTypeNames: MonitoringDataType[];
  unmonitoredTypeNames: MonitoringDataType[];
}

/**
 * Takes a family summary and sorts them in this order:
 * 1. current user
 * 2. onboarded family members, in ascending order by firstName
 * 3. non-onboarded family members, in ascending order by firstName
 *
 * If firstName is null, use displayName.
 * If displayName is null, use email.
 * If email is null, then filter out that family
 *
 * @param state
 * @returns {IFamilyMember[]} Sorted family members
 */
export const getSortedFamilyMembers: (state: IIdentityMonitoringState) => IFamilyMember[] = createSelector(
  [(state: IIdentityMonitoringState) => state.familyMembers],
  (familyMembers) => {
    const compare = (memberA: IFamilyMember, memberB: IFamilyMember) => {
      // Current user first:
      if (memberA.userInfo?.isCurrentUser) {
        return -1;
      }
      if (memberB.userInfo?.isCurrentUser) {
        return 1;
      }

      const memberAOnboarded = memberA.itpOnboardingStatus === 'Monitoring';
      const memberBOnboarded = memberB.itpOnboardingStatus === 'Monitoring';
      if (memberAOnboarded && !memberBOnboarded) {
        return -1;
      }
      if (!memberAOnboarded && memberBOnboarded) {
        return 1;
      }

      const nameA = memberA.userInfo!.firstName! || memberA.userInfo!.displayName! || memberA.userInfo!.email!;
      const nameB = memberB.userInfo!.firstName! || memberB.userInfo!.displayName! || memberB.userInfo!.email!;
      return nameA.localeCompare(nameB);
    };

    return familyMembers
      .filter((member) => !!(member.userInfo?.firstName || member.userInfo?.displayName || member.userInfo?.email))
      .sort(compare);
  }
);

/**
 * Returns protected family members without any sorting.
 *
 * @param state
 * @returns {IFamilyMember[]} Protected family
 */
export const getProtectedFamilyMembers: (state: IIdentityMonitoringState) => IFamilyMember[] = createSelector(
  [(state: IIdentityMonitoringState) => state.familyMembers],
  (familyMembers) => {
    return familyMembers.filter((member) => member.itpOnboardingStatus === 'Monitoring');
  }
);

export const getSelectedFamilyMemberSummary: (state: IIdentityMonitoringState) => IFamilyMember | undefined = createSelector(
  [(state: IIdentityMonitoringState) => state],
  (state: IIdentityMonitoringState) => {
    return state.familyMembers.find(
      (member) =>
        (state.userContext.csid && localeIgnoreCaseEquals(member.csid, state.userContext.csid)) ||
        localeIgnoreCaseEquals(member.userId, state.userContext.userId)
    );
  }
);

/**
 * Returns a family member from the given csid
 *
 * @param state
 * @param csid
 */
export const getITPFamilyMemberFromCsid: (state: IIdentityMonitoringState, csid: string | undefined) => IFamilyMember | undefined =
  createSelector(
    [(state: IIdentityMonitoringState) => state, (state: IIdentityMonitoringState, csid: string | undefined) => csid],
    (state: IIdentityMonitoringState, csid: string | undefined) =>
      csid ? state.familyMembers.find((m) => localeIgnoreCaseEquals(m.csid, csid)) : undefined
  );

/**
 * Returns a family member from the given userId. This can be called when the member doesnt have a csid
 *
 * @param state
 * @param userId
 */
export const getITPFamilyMemberFromUserId: (state: IIdentityMonitoringState, userId: string | undefined) => IFamilyMember | undefined =
  createSelector(
    [(state: IIdentityMonitoringState) => state, (state: IIdentityMonitoringState, userId: string | undefined) => userId],
    (state: IIdentityMonitoringState, userId: string | undefined) =>
      userId ? state.familyMembers.find((m) => localeIgnoreCaseEquals(m.userId, userId)) : undefined
  );

/**
 * Takes a family summary and filters for unmonitored family members
 * with a firstName or displayName (not including current user).
 *
 * @param state
 * @returns {IFamilyMember[]} Summaries for unmonitored family members.
 */
export const getUnmonitoredITPFamilyMembers: (state: IIdentityMonitoringState) => IFamilyMember[] = createSelector(
  [(state) => state.familyMembers],
  (familyMembers) => {
    return familyMembers.filter(
      (member) =>
        member.itpOnboardingStatus === 'Unmonitored' &&
        !member.userInfo?.isCurrentUser &&
        // Must have first name or display name:
        !!(member.userInfo?.firstName || member.userInfo?.displayName)
    );
  }
);

export const getMonitoringDataTypeGroup: (state: IIdentityMonitoringState, csid: string | undefined) => IMonitoringDataTypeGroup =
  createSelector([getITPFamilyMemberFromCsid], (member) => {
    const dataTypeGroup = {
      monitoredTypeNames: [],
      addableTypeNames: [],
      unmonitoredTypeNames: [],
    } as IMonitoringDataTypeGroup;

    if (!member) {
      return dataTypeGroup;
    }

    const { subscriberProfile } = member;

    MonitoringDataTypeNames.forEach((dataType) => {
      const count = subscriberProfile ? getMonitoringDataCount(subscriberProfile, dataType) : 0;
      if (count > 0) {
        dataTypeGroup.monitoredTypeNames.push(dataType);
      }
      if (count < MonitoringDataTypeMetadata[dataType].maxCount) {
        dataTypeGroup.addableTypeNames.push(dataType);
      }
      if (count === 0) {
        dataTypeGroup.unmonitoredTypeNames.push(dataType);
      }
    });

    dataTypeGroup.monitoredTypeNames.sort((a, b) => MonitoringDataTypeMetadata[a].order - MonitoringDataTypeMetadata[b].order);
    dataTypeGroup.addableTypeNames.sort((a, b) => MonitoringDataTypeMetadata[a].order - MonitoringDataTypeMetadata[b].order);
    dataTypeGroup.unmonitoredTypeNames.sort((a, b) => MonitoringDataTypeMetadata[a].order - MonitoringDataTypeMetadata[b].order);

    return dataTypeGroup;
  });

/**
 * Returns current user's {@link IFamilyMember} state
 *
 * @param state
 */
export const getCurrentUser: (state: IIdentityMonitoringState) => IFamilyMember | undefined = createSelector(
  [(state: IIdentityMonitoringState) => state.familyMembers],
  (familyMembers) => familyMembers.find((member) => member.userInfo?.isCurrentUser)
);

/**
 * Returns status for One Time Scan
 *
 * @param state
 */
export const getOneTimeScanStatusData: (state: IIdentityMonitoringState) => {
  scanStatus: OneTimeCyberAgentScanStatusData | undefined;
  statusDataApiStatus: ApiCallResultStatus;
} = createSelector(
  [
    (state: IIdentityMonitoringState) => state.oneTimeScan?.statusData,
    (state: IIdentityMonitoringState) => state.oneTimeScan?.statusDataApiStatus,
  ],
  (scanStatus, statusDataApiStatus) => ({
    scanStatus,
    statusDataApiStatus,
  })
);

/**
 * Returns status for Unauthethenticated One Time Scan
 *
 * @param state
 */
export const getUnauthOneTimeScanStatusData: (state: IIdentityMonitoringState) => {
  scanStatus: UnauthOneTimeCyberAgentScanStatusData | undefined;
  statusDataApiStatus: ApiCallResultStatus;
} = createSelector(
  [
    (state: IIdentityMonitoringState) => state.unauthOneTimeScan?.statusData,
    (state: IIdentityMonitoringState) => state.unauthOneTimeScan?.statusDataApiStatus,
  ],
  (scanStatus, statusDataApiStatus) => ({
    scanStatus,
    statusDataApiStatus,
  })
);
